import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

/**
 * "How much of this order is actually done" — the shared answer.
 *
 * Ordered / awarded / shipped / received are four different denominators that
 * every desk used to eyeball from whatever its own screen had loaded. This
 * renders the numbers the SERVER computed (see services/indent-fulfilment), so
 * the award screen, the vessel form and the indent page cannot disagree about
 * how many containers are still pending.
 */

export interface Fulfilment {
  indentId: string;
  ordered: number;
  awarded: number;
  pendingAward: number;
  overAwarded: number;
  containersKnown: number;
  shipped: number;
  pendingShipment: number;
  unassignedContainerNos: string[];
  received: number;
  inTransit: number;
  byStatus: Record<string, number>;
  batches: Array<{
    id: string;
    batchNo: number;
    quantity: number;
    containerNos: string[];
    shippingLine: string;
    vesselName: string;
    voyageNo: string;
    blNumber: string;
    imoNumber: string;
    mmsi: string;
    etdDeparture: string | null;
    etaArrival: string | null;
    actualArrivalDate: string | null;
    assignedLocalAgent: string;
    status: string;
    createdByName: string;
    createdAt: string | null;
    receivedCount: number;
  }>;
  fullyShipped: boolean;
  fullyReceived: boolean;
}

export function useFulfilment(indentId: string, enabled = true) {
  return useQuery<Fulfilment>({
    queryKey: ['procurement', 'fulfilment', indentId],
    queryFn: () => api.get(`/api/procurement/logistics/${encodeURIComponent(indentId)}/fulfilment`),
    enabled: !!indentId && enabled,
    staleTime: 30_000,
  });
}

export const fmtDate = (s: string | null | undefined) =>
  (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

/** One number with its label. Deliberately plain — this sits inside busy screens. */
function Stat({ label, value, of, tone, hint }: {
  label: string; value: number; of?: number;
  tone?: 'neutral' | 'good' | 'warn' | 'bad'; hint?: string;
}) {
  const colour = tone === 'good' ? 'text-emerald-700'
    : tone === 'warn' ? 'text-amber-700'
      : tone === 'bad' ? 'text-rose-700' : 'text-slate-800';
  return (
    <div className="min-w-[86px]">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`text-lg font-bold leading-tight ${colour}`}>
        {value}
        {of != null && <span className="text-[11px] font-semibold text-slate-400"> / {of}</span>}
      </div>
      {hint && <div className="text-[10px] text-slate-400 leading-tight">{hint}</div>}
    </div>
  );
}

/**
 * The strip of counters. `stage` decides which numbers matter:
 *   award    — ordered vs allocated to vendors vs still to place
 *   shipping — ordered vs on a vessel vs still to book
 *   all      — the whole chain, for the indent page
 */
export function FulfilmentSummary({ f, stage = 'all', selecting }: {
  f: Fulfilment | undefined;
  stage?: 'award' | 'shipping' | 'all';
  /** Live count of what the operator is ticking right now, before saving. */
  selecting?: number;
}) {
  if (!f) return null;
  const showAward = stage === 'award' || stage === 'all';
  const showShip = stage === 'shipping' || stage === 'all';

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
      <div className="flex flex-wrap items-start gap-x-5 gap-y-2">
        <Stat label="Ordered" value={f.ordered} hint="containers" />

        {showAward && (
          <>
            <Stat
              label="Awarded"
              value={f.awarded}
              of={f.ordered}
              tone={f.overAwarded > 0 ? 'bad' : f.pendingAward === 0 ? 'good' : 'warn'}
            />
            <Stat
              label="To award"
              value={f.pendingAward}
              tone={f.pendingAward === 0 ? 'good' : 'warn'}
            />
          </>
        )}

        {showShip && (
          <>
            {selecting != null && (
              <Stat
                label="Selecting"
                value={selecting}
                tone={selecting > f.pendingShipment ? 'bad' : selecting > 0 ? 'good' : 'neutral'}
                hint="this sailing"
              />
            )}
            <Stat
              label="Shipped"
              value={f.shipped}
              of={f.ordered}
              tone={f.fullyShipped ? 'good' : f.shipped > 0 ? 'warn' : 'neutral'}
              hint={f.batches.length ? `${f.batches.length} sailing${f.batches.length === 1 ? '' : 's'}` : undefined}
            />
            <Stat
              label="Pending"
              value={Math.max(0, f.pendingShipment - (selecting || 0))}
              tone={f.pendingShipment - (selecting || 0) === 0 ? 'good' : 'warn'}
              hint="still to book"
            />
          </>
        )}

        {stage === 'all' && (
          <>
            <Stat label="In transit" value={f.inTransit} tone={f.inTransit > 0 ? 'warn' : 'neutral'} />
            <Stat label="Received" value={f.received} of={f.ordered} tone={f.fullyReceived ? 'good' : 'neutral'} />
          </>
        )}
      </div>

      {f.overAwarded > 0 && (
        <div className="mt-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-800">
          {f.overAwarded} container{f.overAwarded === 1 ? '' : 's'} awarded beyond the order quantity — correct the
          award or raise the indent quantity.
        </div>
      )}
    </div>
  );
}

/**
 * The sailings this order is split across.
 *
 * The point of showing them together: an indent with two batches has two ETAs,
 * and reading only the newest one — which is all the indent header can hold —
 * is what used to make containers already at the depot look like they were
 * still on the water.
 */
export function BatchList({ f, onEdit }: { f: Fulfilment | undefined; onEdit?: (batchId: string) => void }) {
  if (!f || !f.batches.length) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead className="border-b bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Batch</th>
            <th className="px-3 py-2 text-left font-semibold">Vessel &middot; voyage</th>
            <th className="px-3 py-2 text-left font-semibold">Line &middot; BL</th>
            <th className="px-3 py-2 text-left font-semibold">ETD</th>
            <th className="px-3 py-2 text-left font-semibold">ETA</th>
            <th className="px-3 py-2 text-right font-semibold">Containers</th>
            <th className="px-3 py-2 text-left font-semibold">Status</th>
            {onEdit && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {f.batches.map((b) => (
            <tr key={b.id} className="align-top hover:bg-slate-50/60">
              <td className="px-3 py-2 font-bold text-slate-700">#{b.batchNo}</td>
              <td className="px-3 py-2">
                <div className="font-semibold text-slate-800">{b.vesselName || '—'}</div>
                {b.voyageNo && <div className="font-mono text-[11px] text-slate-500">{b.voyageNo}</div>}
              </td>
              <td className="px-3 py-2 text-[11px] text-slate-600">
                <div>{b.shippingLine || '—'}</div>
                {b.blNumber && <div className="font-mono text-slate-500">{b.blNumber}</div>}
              </td>
              <td className="px-3 py-2 text-[11px] text-slate-600">{fmtDate(b.etdDeparture)}</td>
              <td className="px-3 py-2 text-[11px] text-slate-600">
                {fmtDate(b.etaArrival)}
                {b.actualArrivalDate && (
                  <div className="text-[10px] font-semibold text-emerald-700">arrived {fmtDate(b.actualArrivalDate)}</div>
                )}
              </td>
              <td className="px-3 py-2 text-right">
                <div className="font-bold text-slate-800">{b.quantity}</div>
                <div className="text-[10px] text-slate-400">{b.receivedCount} received</div>
              </td>
              <td className="px-3 py-2">
                <span className={
                  'rounded-full px-2 py-0.5 text-[10px] font-bold '
                  + (b.status === 'Delivered' ? 'bg-emerald-100 text-emerald-800'
                    : b.status === 'Cancelled' ? 'bg-slate-200 text-slate-600'
                      : b.status === 'Arrived' || b.status === 'Cleared' ? 'bg-sky-100 text-sky-800'
                        : 'bg-amber-100 text-amber-800')
                }>
                  {b.status}
                </span>
              </td>
              {onEdit && (
                <td className="px-3 py-2 text-right">
                  <button
                    className="rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-bold text-slate-600 hover:bg-slate-50"
                    onClick={() => onEdit(b.id)}
                  >
                    Edit
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
